import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, HandCoins, ShoppingCart, Printer } from "lucide-react";

const miktarText = (it) => {
  const kg = Number(it.total_kg || it.kilogram || 0);
  if (it.calc_type && kg > 0) return kg.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " kg";
  return `${it.quantity} ${it.unit || ""}`;
};

export default function CustomerDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get(`/customers/${id}`).then((r) => setD(r.data)); }, [id]);

  if (!d) return <div className="text-center py-20 text-muted-foreground">Yükleniyor...</div>;
  const c = d.customer;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)}><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">{c.name}</h1>
            <p className="text-sm text-muted-foreground">{c.contact} · {c.phone} {c.tax_no && `· VN: ${c.tax_no}`}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => nav(`/satislar/yeni?customer=${id}`)} data-testid="detail-new-sale-btn"><ShoppingCart size={16} className="mr-1" /> Yeni Satış</Button>
          <Button variant="secondary" onClick={() => nav(`/tahsilatlar?new=1&customer=${id}`)} data-testid="detail-collect-btn"><HandCoins size={16} className="mr-1" /> Ödeme Al</Button>
          <Button variant="outline" onClick={() => window.open(`/musteriler/${id}/ekstre`, "_blank")} data-testid="detail-statement-btn"><Printer size={16} className="mr-1" /> Ekstre (PDF)</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Toplam Alışveriş</div>
          <div className="text-3xl font-bold num" data-testid="detail-total-sales">{fmtTL(d.total_sales)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Toplam Ödeme</div>
          <div className="text-3xl font-bold num text-emerald-500" data-testid="detail-total-paid">{fmtTL(d.total_paid)}</div>
        </Card>
        <Card className="p-5 border-primary/40 bg-primary/5">
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Kalan Borç</div>
          <div className={`text-3xl font-bold num ${d.balance > 0 ? "text-primary" : "text-emerald-500"}`} data-testid="detail-balance">{fmtTL(d.balance)}</div>
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="font-bold text-lg mb-3">Cari Hesap Hareketleri</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>İşlem</TableHead>
                <TableHead>Ürün / Açıklama</TableHead>
                <TableHead className="text-right">Miktar</TableHead>
                <TableHead className="text-right">Birim Fiyat</TableHead>
                <TableHead className="text-right">Tutar (₺)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="customer-ledger-table">
              {(() => {
                const led = [...d.ledger].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
                const out = [];
                let gi = 0;
                while (gi < led.length) {
                  const day = (led[gi].date || "").slice(0, 10);
                  const group = [];
                  while (gi < led.length && (led[gi].date || "").slice(0, 10) === day) { group.push(led[gi]); gi++; }
                  let firstRow = true;
                  let dayDebit = 0;
                  group.forEach((e, ei) => {
                    const its = e.type === "satis" ? (e.items || []) : [];
                    dayDebit += Number(e.debit) || 0;
                    if (its.length > 0) {
                      e.items.forEach((it, j) => {
                        out.push(
                          <TableRow key={`${day}-${ei}-${j}`}>
                            <TableCell className="text-sm">{firstRow ? fmtDate(day) : ""}</TableCell>
                            <TableCell>{j === 0 ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-primary/15 text-primary">Satış</span> : ""}</TableCell>
                            <TableCell className="text-sm">{it.product_name}{it.size ? ` · ${it.size}` : ""}</TableCell>
                            <TableCell className="text-right num">{miktarText(it)}</TableCell>
                            <TableCell className="text-right num">{fmtTL(it.unit_price)}</TableCell>
                            <TableCell className="text-right num text-primary">{fmtTL(it.total)}</TableCell>
                          </TableRow>
                        );
                        firstRow = false;
                      });
                      if (e.credit > 0) {
                        out.push(
                          <TableRow key={`${day}-${ei}-pay`}>
                            <TableCell></TableCell>
                            <TableCell><span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/15 text-emerald-500">Tahsilat</span></TableCell>
                            <TableCell className="text-sm" colSpan={3}>Tahsilat (satış ödemesi)</TableCell>
                            <TableCell className="text-right num text-emerald-500">- {fmtTL(e.credit)}</TableCell>
                          </TableRow>
                        );
                      }
                    } else {
                      const isCredit = (e.credit || 0) > 0 && !(e.debit > 0);
                      out.push(
                        <TableRow key={`${day}-${ei}`}>
                          <TableCell className="text-sm">{firstRow ? fmtDate(day) : ""}</TableCell>
                          <TableCell><span className={`px-2 py-0.5 rounded text-xs font-semibold ${isCredit ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary"}`}>{isCredit ? "Tahsilat" : "İşlem"}</span></TableCell>
                          <TableCell className="text-sm" colSpan={3}>{e.desc}</TableCell>
                          <TableCell className={`text-right num ${isCredit ? "text-emerald-500" : "text-primary"}`}>{isCredit ? `- ${fmtTL(e.credit)}` : fmtTL(e.debit)}</TableCell>
                        </TableRow>
                      );
                      firstRow = false;
                    }
                  });
                  out.push(
                    <TableRow key={`${day}-total`} style={{ borderBottom: "3px solid #0f172a" }} className="bg-muted/40">
                      <TableCell></TableCell>
                      <TableCell colSpan={4} className="text-right font-extrabold">{fmtDate(day)} TOPLAM</TableCell>
                      <TableCell className="text-right num font-extrabold">{fmtTL(dayDebit)}</TableCell>
                    </TableRow>
                  );
                }
                return out;
              })()}
              {d.ledger.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Henüz hareket yok</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
